import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { EscortProfile } from '@/types';
import { updateEscortAiSettings } from '@/services/profileService';

interface AiAssistantTabProps {
  profile: EscortProfile;
  onProfileUpdate: (updatedProfile: Partial<EscortProfile>) => void;
}

const AiAssistantTab: React.FC<AiAssistantTabProps> = ({ profile, onProfileUpdate }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState<boolean>(profile.ai_chat_enabled ?? false);
  const [responseMode, setResponseMode] = useState<'always' | 'scheduled'>(profile.ai_chat_response_mode ?? 'always');
  // Utiliser JSON.stringify pour afficher le schedule, nullish coalescing pour valeur par défaut
  const [schedule, setSchedule] = useState<string>(JSON.stringify(profile.ai_chat_schedule ?? {}, null, 2));
  const [isSaving, setIsSaving] = useState(false);

  // Mettre à jour l'état local si le profil externe change
  useEffect(() => {
    setIsEnabled(profile.ai_chat_enabled ?? false);
    setResponseMode(profile.ai_chat_response_mode ?? 'always');
    setSchedule(JSON.stringify(profile.ai_chat_schedule ?? {}, null, 2));
  }, [profile]);

  const handleSaveSettings = async () => {
    if (!user?.id) return;
    setIsSaving(true);

    let parsedSchedule = null;
    if (responseMode === 'scheduled') {
        try {
            parsedSchedule = JSON.parse(schedule);
            // Validation simple du format JSON
            if (typeof parsedSchedule !== 'object' || parsedSchedule === null) {
                 throw new Error("Le format JSON du planning est invalide.")
            }
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('escort.profile.ai.invalid_schedule_json') + (error instanceof Error ? `: ${error.message}` : ''),
                variant: 'destructive',
            });
            setIsSaving(false);
            return;
        }
    }

    try {
      const updates = {
        ai_chat_enabled: isEnabled,
        ai_chat_response_mode: responseMode,
        ai_chat_schedule: responseMode === 'scheduled' ? parsedSchedule : null, // Sauvegarder null si pas en mode scheduled
      };

      await updateEscortAiSettings(user.id, updates);

      // Mettre à jour l'état parent après sauvegarde réussie
      onProfileUpdate(updates);

      toast({
        title: t('common.success'),
        description: t('escort.profile.ai.settings_saved'),
      });
    } catch (error) {
      console.error("Error saving AI settings:", error);
      toast({
        title: t('common.error'),
        description: t('escort.profile.ai.error_saving'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('escort.profile.ai.title')}</CardTitle>
        <CardDescription>{t('escort.profile.ai.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center space-x-2">
          <Switch
            id="ai-enabled"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
          <Label htmlFor="ai-enabled">{t('escort.profile.ai.enable_assistant')}</Label>
        </div>

        {isEnabled && (
          <div className="space-y-4">
            <RadioGroup value={responseMode} onValueChange={(value) => setResponseMode(value as 'always' | 'scheduled')}>
              <Label>{t('escort.profile.ai.response_mode')}</Label>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="always" id="mode-always" />
                <Label htmlFor="mode-always">{t('escort.profile.ai.mode_always')}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="scheduled" id="mode-scheduled" />
                <Label htmlFor="mode-scheduled">{t('escort.profile.ai.mode_scheduled')}</Label>
              </div>
            </RadioGroup>

            {responseMode === 'scheduled' && (
              <div className="space-y-2">
                <Label htmlFor="ai-schedule">{t('escort.profile.ai.schedule_label')}</Label>
                <Textarea
                  id="ai-schedule"
                  placeholder={t('escort.profile.ai.schedule_placeholder')}
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-sm text-muted-foreground">
                    {t('escort.profile.ai.schedule_help')}
                    Example: <code className="font-mono text-xs">{"{\"monday\": [\"09:00-17:00\"], \"tuesday\": [\"10:00-12:00\", \"14:00-18:00\"]}"}</code>
                </p>
              </div>
            )}
          </div>
        )}

        <Button onClick={handleSaveSettings} disabled={isSaving}>
          {isSaving ? t('common.saving') : t('common.save_changes')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AiAssistantTab;